import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Input, Tag, Button, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { DEFAULT_MODAL_RADIUS } from '../constants'; 
interface EditTagsModalProps {
    visible: boolean;
    title?: string;
    initialTags: string[]; // Current tags of the item
    allTags: string[];     // All available tags for autocomplete/quick-add
    onClose: () => void;
    onSave: (tags: string[]) => Promise<void>;
    loading?: boolean;     // External loading state
}

export const EditTagsModal: React.FC<EditTagsModalProps> = ({
    visible,
    title = '编辑标签',
    initialTags,
    allTags,
    onClose,
    onSave,
    loading = false,
}) => {
    const [editingTags, setEditingTags] = useState<string[]>([]);
    const [tagInput, setTagInput] = useState('');
    const [internalSaving, setInternalSaving] = useState(false);

    // Initialize tags when modal opens
    useEffect(() => {
        if (visible) {
            setEditingTags(initialTags);
            setTagInput('');
        }
    }, [visible, initialTags]);

    // Auto-complete suggestion
    const autoCompleteSuggestion = useMemo(() => {
        if (!tagInput.trim()) return null;
        const input = tagInput.toLowerCase();
        const suggestion = allTags.find(
            tag => tag.toLowerCase().startsWith(input) &&
                tag.toLowerCase() !== input &&
                !editingTags.includes(tag)
        );
        return suggestion || null;
    }, [tagInput, allTags, editingTags]);

    // Add tag handler
    const handleAddTag = () => {
        const trimmed = tagInput.trim();
        if (!trimmed) return;

        // Split by comma or whitespace, filter empty and duplicates
        const newTags = trimmed.split(/[,，\s]+/).filter(t => t && !editingTags.includes(t));
        if (newTags.length > 0) {
            setEditingTags([...editingTags, ...newTags]);
        }
        setTagInput('');
    };

    // Keyboard handler
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Tab' && autoCompleteSuggestion) {
            e.preventDefault();
            setTagInput(autoCompleteSuggestion);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    };

    // Remove tag handler
    const handleRemoveTag = (tag: string) => {
        setEditingTags(editingTags.filter(t => t !== tag));
    };

    // Save handler
    const handleSave = async () => {
        setInternalSaving(true);
        try {
            await onSave(editingTags);
            onClose();
        } catch (error) {
            console.error('Update tags failed:', error);
            message.error('更新标签失败');
        } finally {
            setInternalSaving(false);
        }
    };

    const isLoading = loading || internalSaving;

    return (
        <Modal
            title={title}
            open={visible}
            onCancel={onClose}
            onOk={handleSave}
            okText="保存"
            cancelText="取消"
            confirmLoading={isLoading}
            styles={{
                mask: {
                    backdropFilter: 'blur(4px)',
                }
            }}
            // We'll use a hardcoded borderRadius style here to avoid import issues if possible,
            // or just standard antd style. The renderer used DEFAULT_MODAL_RADIUS.
            // Let's search for DEFAULT_MODAL_RADIUS first to be safe.
            style={{ borderRadius: 12, overflow: 'hidden' }}
        >
            <div style={{ marginBottom: 16 }}>
                {editingTags.map(tag => (
                    <Tag
                        key={tag}
                        closable
                        onClose={() => handleRemoveTag(tag)}
                        style={{ marginBottom: 8 }}
                    >
                        {tag}
                    </Tag>
                ))}
            </div>

            <div style={{ marginBottom: 8, color: 'var(--ant-color-text-secondary)' }}>添加标签 (可选)</div>
            <div style={{ position: 'relative', width: '100%' }}>
                <Input
                    placeholder="输入标签后按回车添加"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{ width: '100%', paddingRight: 36, background: 'transparent' }}
                />
                {/* Auto-complete overlay */}
                {autoCompleteSuggestion && tagInput && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 36,
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: 11,
                            pointerEvents: 'none',
                            overflow: 'hidden',
                        }}
                    >
                        <span style={{ color: 'transparent' }}>{tagInput}</span>
                        <span style={{ color: 'var(--ant-color-text-quaternary)' }}>
                            {autoCompleteSuggestion.slice(tagInput.length)}
                        </span>
                        <span style={{
                            marginLeft: 8,
                            fontSize: 11,
                            color: 'var(--ant-color-msg-content)', // approximate placeholder
                            backgroundColor: 'rgba(150, 150, 150, 0.1)',
                            padding: '2px 6px',
                            borderRadius: 3,
                        }}>
                            按 Tab 键自动补全
                        </span>
                    </div>
                )}
                <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={handleAddTag}
                    style={{
                        fontSize: 14,
                        position: 'absolute',
                        right: 0,
                        color: 'var(--ant-color-text)',
                        padding: '0px 16px',
                    }}
                />
            </div>

            {allTags.length > 0 && (
                <div style={{ marginTop: 24 }}>
                    <div style={{ marginBottom: 12, color: 'var(--ant-color-text-tertiary)', fontSize: '14px' }}>
                        快速添加:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {allTags
                            .filter(tag => !editingTags.includes(tag))
                            .map(tag => (
                                <Tag
                                    key={tag}
                                    onClick={() => setEditingTags([...editingTags, tag])}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: 'var(--ant-tag-default-bg)',
                                        border: '1px solid transparent',
                                        marginRight: 0,
                                        userSelect: 'none',
                                        transition: 'all 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.color = 'var(--ant-color-primary)';
                                        e.currentTarget.style.backgroundColor = 'var(--ant-color-primary-bg)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.color = '';
                                        e.currentTarget.style.backgroundColor = 'var(--ant-tag-default-bg)';
                                    }}
                                >
                                    {tag}
                                </Tag>
                            ))}
                        {allTags.filter(tag => !editingTags.includes(tag)).length === 0 && (
                            <span style={{ color: 'var(--ant-color-text-tertiary)', fontSize: '13px' }}>
                                暂无更多可选标签
                            </span>
                        )}
                    </div>
                </div>
            )}
        </Modal>
    );
};
